import { useEffect, useState, useRef } from 'react'
import useApi from '../api';
import { useAimdoc } from '@aimdoc/sdk-react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import 'react-toastify/dist/ReactToastify.css';

import { FormField, ScoringCategory } from '../types'
import Spinner from '../documentation/Spinner';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import DeployAgent from './DeployAgent';
import useAnalytics from '../analytics/analytics';
import { conLog } from '../utils/conLog';
import { IconList } from '../ui/heroicons-picker/iconType';
import { useUser } from '../user/UserContext';
import { useSearchParams } from 'react-router-dom';
import { AimdocComponentContext } from '@aimdoc/sdk-react';
import { DEFAULT_AGENT_FONT_ID } from '../../../../widget/src/agent/fontCatalog';

// shadcn UI components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import AimdocLoader from '../ui/AimdocLoader';


// Icons
import {  
    FileText, 
    Palette, 
    Zap,
    Bot,
    ChevronLeft,
    ChevronDown,
    ChevronRight,
    Settings,
    Brain,
    Globe,
    Rocket,
    Mic,
    GitBranch,
    Radar,
    Send,
    Mail,
    Lock,
    ListChecks,
    Sparkles,
    Target,
    Plug,
} from 'lucide-react';

// Import tab components
import InstructionsTab from './tabs/InstructionsTab';
import Website from './builder/Website';
import Files from './builder/Files';
import CustomizationTab from './tabs/CustomizationTab';
import AgentGatewayTab from './tabs/AgentGatewayTab';
import EmailChannelTab from './tabs/EmailChannelTab';
import ProactiveTab from './tabs/ProactiveTab';
import DataCaptureTab from './tabs/DataCaptureTab';
import WorkflowTab from './tabs/WorkflowTab';
import SkillsTab from './tabs/SkillsTab';
import ObjectivesTab from './tabs/ObjectivesTab';
import ActionsTab from './tabs/ActionsTab';
import McpServersTab from './tabs/McpServersTab';
import AppearanceTab from './tabs/AppearanceTab';
import VoiceTab from './tabs/VoiceTab';
import AppPromptingTab from './tabs/AppPromptingTab';
import AppConfigurationTab from './tabs/AppConfigurationTab';
import AppAppearanceTab from './tabs/AppAppearanceTab';
import TaskDefinitionsTab from './tabs/TaskDefinitionsTab';
import {
    AgentAppOverrides,
    normalizeAppOverrides,
    pruneAppOverrides,
    countOverrides,
    PROMPTING_OVERRIDE_KEYS,
    CUSTOMIZATION_OVERRIDE_KEYS,
    APPEARANCE_OVERRIDE_KEYS,
} from './tabs/appOverrides';
import {
    AgentSkill,
    OutcomeDefinition,
    VoiceOption,
    VisitorTrackNode,
    isConditionalSkillMissingRules,
} from './tabs/types';
import PreviewAgent from './PreviewAgent';
import Upgrade from '../modals/Upgrade';
import { getApiErrorMessage, getRawApiDetail } from './saveErrors';
import AgentEditorHeaderActions from './AgentEditorHeaderActions';

interface EditorProps {
    docId?: string | undefined;
    agentId?: string | undefined;
    fields?: FormField[];
    modalRef?: any;
    toggleModal?: any
    setAgents?: any
    modalVisible?: boolean
}

// Preview is a fixed 400px column. Hide it on small layouts, and whenever the
// Aimdoc sidebar is open — that push is what actually crushes the editor on a laptop.
// Budget: 224px tab nav + 32px gutters + 400px preview leaves the tab content
// ~560px at this threshold, which is the narrowest the tabs still read well.
const PREVIEW_MIN_LAYOUT_PX = 1280;

const isAimdocSidebarOpen = () => {
    if (typeof window === 'undefined') return false;
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--aimdoc-sidebar-width')
        .trim();
    return Number.parseFloat(raw) > 0;
};

// Sidebar sections. The agent builder groups tabs by mode so customers can
// find website-vs-in-app settings without a bulky flat list. Sections are
// rendered as uppercase labels with the tabs under each.
type SidebarTab = {
    name: string;
    icon: React.ReactNode;
    label: string;
    // When true, the tab sits inside the In-app section and shows override-count badges.
    inApp?: boolean;
    // Key group used to count overrides on the sidebar badge.
    overrideGroup?: 'prompting' | 'customization' | 'appearance';
    // When true, this tab renders a nested accordion (e.g. knowledge/websites+documents).
    accordion?: boolean;
    // When true, the tab is hidden from the sidebar nav (feature not ready to ship).
    hidden?: boolean;
};

type SidebarSection = {
    id: 'website' | 'in_app' | 'shared';
    label?: string;
    tabs: SidebarTab[];
    licenseGated?: 'activate';
};

const TAB_SECTIONS: SidebarSection[] = [
    {
        id: 'website',
        label: 'Website mode',
        tabs: [
            { name: 'instructions', icon: <Bot className="w-4 h-4" />, label: 'Prompting' },
            { name: 'customization', icon: <Settings className="w-4 h-4" />, label: 'Configuration' },
            { name: 'appearance', icon: <Palette className="w-4 h-4" />, label: 'Appearance' },
            { name: 'agent_gateway', icon: <Radar className="w-4 h-4" />, label: 'Agent Gateway' },
            { name: 'workflow', icon: <GitBranch className="w-4 h-4" />, label: 'Workflow' },
        ],
    },
    {
        id: 'in_app',
        label: 'App mode',
        licenseGated: 'activate',
        tabs: [
            { name: 'app_prompting', icon: <Bot className="w-4 h-4" />, label: 'Prompting', inApp: true, overrideGroup: 'prompting' },
            { name: 'app_configuration', icon: <Settings className="w-4 h-4" />, label: 'Configuration', inApp: true, overrideGroup: 'customization' },
            { name: 'app_appearance', icon: <Palette className="w-4 h-4" />, label: 'Appearance', inApp: true, overrideGroup: 'appearance' },
            { name: 'app_tasks', icon: <ListChecks className="w-4 h-4" />, label: 'Task Definitions', inApp: true },
        ],
    },
    {
        id: 'shared',
        tabs: [
            { name: 'knowledge', icon: <Brain className="w-4 h-4" />, label: 'Knowledge', accordion: true },
            { name: 'data_capture', icon: <FileText className="w-4 h-4" />, label: 'Data Capture' },
            { name: 'skills', icon: <Sparkles className="w-4 h-4" />, label: 'Skills', hidden: false },
            { name: 'objectives', icon: <Target className="w-4 h-4" />, label: 'Objectives' },
            { name: 'actions', icon: <Zap className="w-4 h-4" />, label: 'Actions' },
            { name: 'mcp', icon: <Plug className="w-4 h-4" />, label: 'MCP Servers' },
            { name: 'voice', icon: <Mic className="w-4 h-4" />, label: 'Voice' },
            { name: 'email', icon: <Mail className="w-4 h-4" />, label: 'Email' },
            { name: 'proactive', icon: <Send className="w-4 h-4" />, label: 'Proactive' },
            { name: 'deploy', icon: <Rocket className="w-4 h-4" />, label: 'Deploy' },
        ],
    },
];

// Migrate any URL/tab references to the retired `app_mode` tab so deep links
// keep working after the restructure.
function normalizeTabName(raw: string | null | undefined): string | null {
    if (!raw) return null;
    if (raw === 'form') return 'data_capture';
    if (raw === 'app_mode') return 'app_configuration';
    if (raw === 'assessments') return 'instructions';
    return raw;
}

// Renders the license-locked state shown when the current plan doesn't
// include in-app mode. Matches the previous AppMode upgrade interstitial.
function InAppLicenseGate({ onUpgrade }: { onUpgrade: () => void }) {
    return (
        <div className="rounded-lg border bg-card px-6 py-10 text-center">
            <Lock className="mx-auto h-5 w-5 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-semibold">App mode requires Activate</h3>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-muted-foreground">
                Configure task definitions, app-specific prompting, appearance, and more.
            </p>
            <Button size="sm" className="mt-4" onClick={onUpgrade}>
                View plans
            </Button>
        </div>
    );
}

// Shown on In-app Prompting/Appearance when neither a Main App URL nor any
// allowlist URLs are set, so the widget would never actually enter in-app
// mode. Task Definitions intentionally stays reachable because they are
// agent-scoped and useful regardless of URL matching.
function InAppUrlsGate({ onGoToConfig }: { onGoToConfig: () => void }) {
    return (
        <div className="rounded-lg border bg-muted/20 p-6 flex flex-col items-center text-center gap-3">
            <div>
                <h3 className="text-sm font-semibold">Set your Main App URL to enable app mode</h3>
                <p className="text-xs text-muted-foreground max-w-md mt-1">
                    App mode overrides only apply when the widget detects it's running on a URL you've marked
                    as your app. Add a Main App URL (and optional allow-list URLs) in Configuration to unlock
                    this section.
                </p>
            </div>
            <Button size="sm" onClick={onGoToConfig}>
                Go to Configuration
            </Button>
        </div>
    );
}

type SaveIssueSection = {
    title: string;
    items: string[];
};

type SaveIssueModalContent = {
    title: string;
    message: string;
    because: string[];
    sections: SaveIssueSection[];
};

type VisibilityRuleConfig = {
    url?: unknown;
    type?: unknown;
    countries?: unknown;
    [key: string]: unknown;
};

const ORPHAN_TRACK_ERROR_CODE = 'invalid_track_field_references';
const ORPHAN_TOOL_MAPPING_ERROR_CODE = 'invalid_tool_field_mappings';

function cleanStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
}

function normalizeVisibilityRules(rules: unknown[]): VisibilityRuleConfig[] {
    return rules.map((rule) => {
        if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
            return { url: '' };
        }

        const normalizedRule = rule as Record<string, unknown>;

        if (normalizedRule.type === 'country') {
            const countries = Array.isArray(normalizedRule.countries)
                ? normalizedRule.countries
                    .map((code) => (typeof code === 'string' ? code.trim().toUpperCase() : ''))
                    .filter((code, index, arr) => /^[A-Z]{2}$/.test(code) && arr.indexOf(code) === index)
                : [];
            const { url: _url, condition: _condition, ...rest } = normalizedRule;
            return { ...rest, type: 'country', countries };
        }

        const url = typeof normalizedRule.url === 'string'
            ? normalizedRule.url.trim()
            : '';

        return { ...normalizedRule, url };
    });
}

function normalizeOutcomesConfig(raw: unknown): OutcomeDefinition[] {
    const items = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object' && Array.isArray((raw as any).items)
            ? (raw as any).items
            : [];

    return (items as unknown[])
        .filter((item: unknown): item is Record<string, unknown> => (
            Boolean(item) && typeof item === 'object' && !Array.isArray(item)
        ))
        .map((item: Record<string, unknown>, index: number) => ({
            name: typeof item.name === 'string' && item.name.trim()
                ? item.name.trim()
                : `outcome_${index + 1}`,
            label: typeof item.label === 'string' ? item.label : '',
            description: typeof item.description === 'string' ? item.description : '',
            active: item.active === undefined ? true : Boolean(item.active),
            sort_order: typeof item.sort_order === 'number' ? item.sort_order : index,
        }));
}

function getSaveIssueModalContent(error: any): SaveIssueModalContent | null {
    const detail = getRawApiDetail(error);
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return null;
    }

    const code = typeof detail.code === 'string' ? detail.code : '';
    const message =
        typeof detail.message === 'string'
            ? detail.message
            : 'Please fix the issues below before saving again.';

    const becauseRaw = detail.because ?? detail.Because;
    const because = Array.isArray(becauseRaw)
        ? cleanStringList(becauseRaw)
        : typeof becauseRaw === 'string' && becauseRaw.trim()
            ? [becauseRaw.trim()]
            : [];

    if (code === ORPHAN_TOOL_MAPPING_ERROR_CODE) {
        const issues = Array.isArray(detail.issues) ? detail.issues : [];
        const items = issues
            .map((issue: any) => {
                if (!issue || typeof issue !== 'object') return '';
                const integration =
                    (typeof issue.integration === 'string' && issue.integration.trim()) ||
                    (typeof issue.tool === 'string' && issue.tool.trim()) ||
                    'Integration';
                const objectLabel =
                    typeof issue.object === 'string' && issue.object.trim()
                        ? ` (${issue.object.trim()})`
                        : '';
                const missing = cleanStringList(issue.missing_field_mapping_keys);
                if (!missing.length) return '';
                return `${integration}${objectLabel}: remove or remap ${missing.join(', ')}`;
            })
            .filter(Boolean);

        if (!items.length && !because.length) return null;
        return {
            title: "Can't save yet",
            message,
            because,
            sections: items.length
                ? [{ title: 'Orphaned integration mappings', items }]
                : [],
        };
    }

    if (code === ORPHAN_TRACK_ERROR_CODE) {
        const issues = Array.isArray(detail.issues) ? detail.issues : [];
        const items = issues
            .map((issue: any) => {
                if (!issue || typeof issue !== 'object') return '';
                const trackName =
                    (typeof issue.track_name === 'string' && issue.track_name.trim()) ||
                    (typeof issue.track_system_name === 'string' && issue.track_system_name.trim()) ||
                    'Unnamed track';
                const missingCollect = cleanStringList(issue.missing_fields_to_collect);
                const missingRules = cleanStringList(issue.missing_routing_rule_fields);
                const parts: string[] = [];
                if (missingCollect.length) {
                    parts.push(`fields to collect: ${missingCollect.join(', ')}`);
                }
                if (missingRules.length) {
                    parts.push(`routing rules: ${missingRules.join(', ')}`);
                }
                if (!parts.length) return '';
                return `${trackName}: ${parts.join(' | ')}`;
            })
            .filter(Boolean);

        if (!items.length && !because.length) return null;
        return {
            title: "Can't save yet",
            message,
            because,
            sections: items.length
                ? [{ title: 'Orphaned fields in Visitor Workflow', items }]
                : [],
        };
    }

    if (because.length) {
        return {
            title: "Can't save yet",
            message,
            because,
            sections: [],
        };
    }

    return null;
}

export default function ConfigureAgent({ docId, agentId, fields, modalRef, toggleModal, setAgents, modalVisible }: EditorProps) {
    const { id } = useParams();
    const [agId, setAgId] = useState<string | undefined>(() => {
        if (agentId) {
            return agentId;
        } else {
            if (window.location.pathname.includes('agents')) {
                return id;
            }
        }
    });
    const [links, setLinks] = useState<any>([]);
    const [starters, setStarters] = useState<any>([]);
    const [agentName, setAgentName] = useState<string>('');
    const [agentDescription, setAgentDescription] = useState<string>('');
    const [goal, setGoal] = useState<string>('');
    const [persona, setPersona] = useState<string>('');
    const [userInput, setUserInput] = useState('');
    const [initialLoading, setInitialLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedFields, setSelectedFields] = useState<FormField[] | undefined>([]);
    const [editName, setEditName] = useState(false);
    const [needsUpdate, setNeedsUpdate] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [fileChanged, setFileChanged] = useState(false);
    const [iconFileChanged, setIconFileChanged] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [iconPreviewUrl, setIconPreviewUrl] = useState('');
    const [color, setColor] = useState("#000000");
    const [endColor, setEndColor] = useState("#000000");
    const [isColorValid, setIsColorValid] = useState(true);
    const [isEndColorValid, setIsEndColorValid] = useState(true);
    const [website, setWebsite] = useState<string>('');
    const [indexedPages, setIndexedPages] = useState<any[]>([]);
    const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
    const [colorScheme, setColorScheme] = useState<string>('Solid');
    const [welcomeMessage, setWelcomeMessage] = useState<string>('Welcome to our website! How can I help you today?');
    const [font, setFont] = useState<string>(DEFAULT_AGENT_FONT_ID);
    const [refreshPreview, setRefreshPreview] = useState<boolean>(false);
    const [autoOpenEnabled, setAutoOpenEnabled] = useState<boolean>(false);
    const [openOncePerSession, setOpenOncePerSession] = useState<boolean>(false);
    const [enableFollowUps, setEnableFollowUps] = useState<boolean>(false);
    const [followUpInstructions, setFollowUpInstructions] = useState<string>('');
    const [enableVisitorLocation, setEnableVisitorLocation] = useState<boolean>(false);
    const [enablePageAwareness, setEnablePageAwareness] = useState<boolean>(false);
    const [smartNavigationEnabled, setSmartNavigationEnabled] = useState<boolean>(false);
    const [autoOpenSeconds, setAutoOpenSeconds] = useState<number>(0);
    const [popupMessageEnabled, setPopupMessageEnabled] = useState<boolean>(false);
    const [contextualPopupEnabled, setContextualPopupEnabled] = useState<boolean>(true);
    const [contextualStartersEnabled, setContextualStartersEnabled] = useState<boolean>(true);
    const [chatbotEnabled, setChatbotEnabled] = useState<boolean>(true);
    const [mobileEnabled, setMobileEnabled] = useState<boolean>(true);
    const [searchBarEnabled, setSearchBarEnabled] = useState<boolean>(true);
    const [searchBarPlaceholder, setSearchBarPlaceholder] = useState<string>('👋 Ask AI');
    const [hideAimdoc, setHideAimdoc] = useState<boolean>(false);
    const [popupMessage, setPopupMessage] = useState<string>('');
    // Proactive engagement (see ProactiveTab); numeric fields kept as strings
    // for free typing, normalized at save.
    const [proactiveEnabled, setProactiveEnabled] = useState<boolean>(false);
    const [proactiveMinInactivityHours, setProactiveMinInactivityHours] = useState<string>('12');
    const [proactiveMaxUnanswered, setProactiveMaxUnanswered] = useState<string>('3');
    const [proactiveEmailFallback, setProactiveEmailFallback] = useState<boolean>(false);
    const [proactiveInstructions, setProactiveInstructions] = useState<string>('');
    const [proactiveVisitorsEnabled, setProactiveVisitorsEnabled] = useState<boolean>(false);
    const [proactiveVisitorsMinInactivityHours, setProactiveVisitorsMinInactivityHours] = useState<string>('12');
    const [proactiveVisitorsMaxUnanswered, setProactiveVisitorsMaxUnanswered] = useState<string>('2');
    const [proactiveVisitorsDailyCap, setProactiveVisitorsDailyCap] = useState<string>('50');
    const [proactiveVisitorsInstructions, setProactiveVisitorsInstructions] = useState<string>('');
    const [agentIconType, setAgentIconType] = useState<string>('Icon');
    const [agentIcon, setAgentIcon] = useState<IconList>('FireIcon');
    const [draftId, setDraftId] = useState<string | undefined>();
    const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | null>(null);
    const [form, setForm] = useState<any[]>([]);
    const [outcomes, setOutcomes] = useState<OutcomeDefinition[]>([]);
    const [trackNodes, setTrackNodes] = useState<VisitorTrackNode[]>([]);
    const [enableTracks, setEnableTracks] = useState<boolean>(false);
    const [skills, setSkills] = useState<AgentSkill[]>([]);
    const [enableSkills, setEnableSkills] = useState<boolean>(false);
    const [scoringCategories, setScoringCategories] = useState<ScoringCategory[]>([]);
    const [qualifyVisitor, setQualifyVisitor] = useState<boolean>(false);
    const [actions, setActions] = useState<Record<string, any>>({});
    const [enableCopilot, setEnableCopilot] = useState<boolean>(false);
    const [escalationEnabled, setEscalationEnabled] = useState<boolean>(false);
    const [escalationButtons, setEscalationButtons] = useState<any[]>([]);
    const [gdprEnabled, setGdprEnabled] = useState<boolean>(false);
    const [workEmailOnly, setWorkEmailOnly] = useState<boolean>(false);
    const [requireContactInfo, setRequireContactInfo] = useState<boolean>(false);
    const [messagesBeforeContactInfo, setMessagesBeforeContactInfo] = useState<number>(5);
    const [hideContactUsButton, setHideContactUsButton] = useState<boolean>(false);
    const [rateLimitMessages, setRateLimitMessages] = useState<number>(20);
    const [rateLimitSeconds, setRateLimitSeconds] = useState<number>(240);
    const [rateLimitMessage, setRateLimitMessage] = useState<string>('You have reached the message limit. Please wait before sending another message.');
    const [agentGatewayEnabled, setAgentGatewayEnabled] = useState<boolean>(false);
    const [agentGatewayDailyCap, setAgentGatewayDailyCap] = useState<number>(500);
    const [agentGatewaySites, setAgentGatewaySites] = useState<string[]>([]);
    const [visibilityRules, setVisibilityRules] = useState<any[]>([]);
    const [contextualExclusionRules, setContextualExclusionRules] = useState<any[]>([]);
    const [showGloballyAfterEngagement, setShowGloballyAfterEngagement] = useState<boolean>(false);
    const [widgetPosition, setWidgetPosition] = useState<string>('right');
    const [disclaimerMessage, setDisclaimerMessage] = useState<string>('');
    const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string>('');
    const [collapsedMode, setCollapsedMode] = useState<'chat' | 'copilot'>('chat');
    const [expandedMode, setExpandedMode] = useState<'chat' | 'copilot' | 'sidebar'>('chat');
    const [voiceEnabled, setVoiceEnabled] = useState<boolean>(false);
    const [selectedVoice, setSelectedVoice] = useState<VoiceOption | null>(null);
    const [onboardingUrl, setOnboardingUrl] = useState<string>('');
    const [onboardingAllowlistUrls, setOnboardingAllowlistUrls] = useState<string[]>([]);
    const [onlyShowForAdmin, setOnlyShowForAdmin] = useState<boolean>(false);
    const [darkModeEnabled, setDarkModeEnabled] = useState<boolean>(false);
    const [darkModeDefault, setDarkModeDefault] = useState<'system' | 'light' | 'dark'>('system');
    const [darkModeColor, setDarkModeColor] = useState<string>('#111111');
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
    const [saveIssueModalOpen, setSaveIssueModalOpen] = useState(false);
    const [saveIssueModalContent, setSaveIssueModalContent] = useState<SaveIssueModalContent | null>(null);
    
    const { get, post, put, remove } = useApi();

    const navigate = useNavigate();
    const location = useLocation();
    const { captureEvent } = useAnalytics();
    const { checkLicenseType, organization } = useUser();
    const { sendMessage } = useAimdoc();
    const [expandedModeSections, setExpandedModeSections] = useState({
        website: true,
        in_app: true,
    });

    const onboardingMessageSentRef = useRef(false);
    const builderLayoutRef = useRef<HTMLDivElement>(null);
    const [builderLayoutWidth, setBuilderLayoutWidth] = useState(
        () => (typeof window === 'undefined' ? PREVIEW_MIN_LAYOUT_PX : window.innerWidth)
    );
    const [aimdocSidebarOpen, setAimdocSidebarOpen] = useState(isAimdocSidebarOpen);
    const showPreview = !aimdocSidebarOpen && builderLayoutWidth >= PREVIEW_MIN_LAYOUT_PX;

    useEffect(() => {
        const el = builderLayoutRef.current;
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            setBuilderLayoutWidth(width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [initialLoading]);

    useEffect(() => {
        const onSidebar = (event: Event) => {
            const detail = (event as CustomEvent<{ open?: boolean }>).detail;
            setAimdocSidebarOpen(Boolean(detail?.open));
        };
        window.addEventListener('aimdoc:sidebar', onSidebar);
        return () => window.removeEventListener('aimdoc:sidebar', onSidebar);
    }, []);

    // App mode starts collapsed on plans where it is unavailable. It remains
    // user-expandable so customers can still inspect the locked capabilities.
    useEffect(() => {
        if (organization && !checkLicenseType('activate')) {
            setExpandedModeSections((current) =>
                current.in_app ? { ...current, in_app: false } : current
            );
        }
    }, [checkLicenseType, organization]);

    const [appOverrides, setAppOverrides] = useState<AgentAppOverrides>({});

    // Whether in-app URL detection is actually configured. When false, the
    // In-app Prompting/Appearance tabs render an interstitial prompting the
    // user to set a Main App URL rather than showing a pile of override
    // fields that would never take effect.
    const hasInAppUrls = Boolean(
        (onboardingUrl && onboardingUrl.trim().length > 0)
        || (Array.isArray(onboardingAllowlistUrls)
            && onboardingAllowlistUrls.some((u) => typeof u === 'string' && u.trim().length > 0))
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const searchTab = searchParams.get('tab');
    const normalizedInitialTab = normalizeTabName(searchTab);
    const [selectedTab, setSelectedTab] = useState<string | null>(
        normalizedInitialTab === 'knowledge' ? 'knowledge:websites' : normalizedInitialTab
    );
    const [accordionValue, setAccordionValue] = useState<string | undefined>(
        normalizedInitialTab === 'knowledge' || normalizedInitialTab?.startsWith('knowledge:') ? 'knowledge' : undefined
    );

    const handleTabChange = (value: string) => {
        setSelectedTab(value);
        if (searchTab !== value) {
            setSearchParams({ tab: value });
        }
        
        // Keep accordion open if a knowledge sub-tab is selected
        if (value.startsWith('knowledge:')) {
            setAccordionValue('knowledge');
        }
    };
    
    const handleAccordionChange = (value: string | undefined) => {
        // Only collapse if clicking the accordion trigger itself, not when selecting sub-tabs
        if (value === undefined && !selectedTab?.startsWith('knowledge:')) {
            setAccordionValue(value);
        } else if (value === 'knowledge') {
            setAccordionValue(value);
            // If opening the accordion and no sub-tab is selected, select the first one
            if (!selectedTab?.startsWith('knowledge:')) {
                handleTabChange('knowledge:websites');
            }
        }
    };

    useEffect(() => {
        const tab = searchTab || "instructions";
        const normalizedTab = normalizeTabName(tab) || 'instructions';
        const nextTab = normalizedTab === 'knowledge' ? 'knowledge:websites' : normalizedTab;
        const nextAccordion = normalizedTab === 'knowledge' || normalizedTab?.startsWith('knowledge:') ? 'knowledge' : undefined;
        setSelectedTab((prev) => (prev === nextTab ? prev : nextTab));
        setAccordionValue((prev) => (prev === nextAccordion ? prev : nextAccordion));
    }, [searchTab]);

    const createAgent = async (silent?: boolean) => {
        if (saving) return;

        const colors = compareColors(color, endColor);
        if (agentName === "") {
            toast.error('Agent name is required.', toastConfig);
            return;
        }
        if (isColorValid === false) {
            toast.error('First color is not valid.', toastConfig);
            return;
        };
        if (isEndColorValid === false) {
            toast.error('Second color is not valid.', toastConfig);
            return;
        }
        if (agentIconType === 'Image' && !iconPreviewUrl) {
            toast.error('Please upload an icon image.', toastConfig);
            return;
        }
        if (voiceEnabled && !selectedVoice) {
            toast.error('Please select a voice when voice is enabled.', toastConfig);
            return;
        }

        if (enableSkills) {
            const incompleteConditionalSkills = (skills || []).filter(isConditionalSkillMissingRules);
            if (incompleteConditionalSkills.length > 0) {
                const names = incompleteConditionalSkills
                    .map((skill) => skill.name?.trim() || 'Unnamed skill')
                    .join(', ');
                const message = incompleteConditionalSkills.length === 1
                    ? `"${names}" is conditional but has no rules. Add at least one condition, or switch it to Always active.`
                    : `These skills are conditional but have no rules: ${names}. Add conditions or switch them to Always active.`;
                toast.error(message, toastConfig);
                return;
            }
        }

        const normalizedVisibilityRules = normalizeVisibilityRules(visibilityRules);
        const incompleteVisibilityRuleIndex = normalizedVisibilityRules.findIndex((rule) => {
            if (rule.type === 'country') {
                return !Array.isArray(rule.countries) || rule.countries.length === 0;
            }
            return typeof rule.url !== 'string' || rule.url.length === 0;
        });
        if (incompleteVisibilityRuleIndex !== -1) {
            const incompleteRule = normalizedVisibilityRules[incompleteVisibilityRuleIndex];
            const message = incompleteRule.type === 'country'
                ? `Visibility rule ${incompleteVisibilityRuleIndex + 1} needs at least one country before saving.`
                : `Visibility rule ${incompleteVisibilityRuleIndex + 1} needs a URL or URL fragment before saving.`;
            if (!silent) {
                toast.error(message, toastConfig);
            }
            throw new Error(message);
        }

        const normalizedOnboardingAllowlistUrls = onboardingAllowlistUrls
            .map((url) => url.trim())
            .filter((url, index, arr) => url.length > 0 && arr.indexOf(url) === index);
        const normalizedWebsite = typeof website === 'string' ? website.trim() : '';

        const data = {
            fields: selectedFields,
            name: agentName,
            description: agentDescription,
            goal: goal,
            type: "website",
            persona: persona,
            tools: actions,
            links: links,
            starters: starters,
            config: {
                font: font,
                scheme: colorScheme,
                color: color,
                endColor: endColor,
                mixedColor: mixColors(color, endColor),
                darkerColor: colors.darkerColor,
                lighterColor: colors.lighterColor,
                welcomeMessage: welcomeMessage,
                autoOpenEnabled: autoOpenEnabled,
                autoOpenSeconds: autoOpenSeconds,
                openOncePerSession: openOncePerSession,
                popupMessageEnabled: popupMessageEnabled,
                popupMessage: popupMessage,
                proactiveEnabled: proactiveEnabled,
                proactiveMinInactivityHours: Math.min(720, Math.max(1, Math.floor(Number(proactiveMinInactivityHours)) || 12)),
                proactiveMaxUnanswered: Math.min(20, Math.max(1, Math.floor(Number(proactiveMaxUnanswered)) || 3)),
                proactiveEmailFallback: proactiveEmailFallback,
                proactiveInstructions: proactiveInstructions,
                proactiveVisitorsEnabled: proactiveVisitorsEnabled,
                proactiveVisitorsMinInactivityHours: Math.min(720, Math.max(1, Math.floor(Number(proactiveVisitorsMinInactivityHours)) || 12)),
                proactiveVisitorsMaxUnanswered: Math.min(20, Math.max(1, Math.floor(Number(proactiveVisitorsMaxUnanswered)) || 2)),
                proactiveVisitorsDailyCap: Math.min(1000, Math.max(1, Math.floor(Number(proactiveVisitorsDailyCap)) || 50)),
                proactiveVisitorsInstructions: proactiveVisitorsInstructions,
                contextualPopupEnabled: contextualPopupEnabled,
                contextualStartersEnabled: contextualStartersEnabled,
                chatbotEnabled: chatbotEnabled,
                mobileEnabled: mobileEnabled,
                searchBarEnabled: searchBarEnabled,
                searchBarPlaceholder: searchBarPlaceholder,
                agentMessageIconType: agentIconType,
                agentMessageIcon: agentIcon,
                enableFollowUps: enableFollowUps,
                followUpInstructions: followUpInstructions,
                enableVisitorLocation: enableVisitorLocation,
                enablePageAwareness: enablePageAwareness,
                smartNavigationEnabled: smartNavigationEnabled,
                hideAimdoc: hideAimdoc,
                form: form,
                outcomes: { version: 1, items: outcomes },
                enableTracks: enableTracks,
                tracks: trackNodes?.length
                    ? { version: 1, nodes: trackNodes }
                    : undefined,
                enableSkills: enableSkills,
                skills: skills?.length ? skills : undefined,
                scoringCategories: scoringCategories,
                qualifyVisitor: qualifyVisitor,
                enableCopilot: enableCopilot,
                escalationEnabled: escalationEnabled,
                escalationButtons: escalationButtons,
                gdprEnabled: gdprEnabled,
                workEmailOnly: workEmailOnly,
                requireContactInfo: requireContactInfo,
                messagesBeforeContactInfo: messagesBeforeContactInfo,
                hideContactUsButton: hideContactUsButton,
                security: {
                    rateLimitMessages: rateLimitMessages,
                    rateLimitSeconds: rateLimitSeconds,
                    rateLimitMessage: rateLimitMessage,
                },
                agentGateway: {
                    enabled: agentGatewayEnabled,
                    dailyMessageCap: Math.max(1, Math.floor(agentGatewayDailyCap) || 500),
                    sites: agentGatewaySites.map((s) => s.trim()).filter(Boolean),
                },
                visibilityRules: normalizedVisibilityRules,
                // URL carve-outs where contextual starters/pop-ups fall back to
                // the defaults (AIM-501). Blank rows are dropped rather than
                // rejected: an empty rule can never match anything.
                contextualExclusionRules: contextualExclusionRules
                    .filter((rule) => rule && typeof rule === 'object' && typeof rule.url === 'string' && rule.url.trim())
                    .map((rule) => ({ ...rule, url: rule.url.trim() })),
                // The "show globally after engagement" toggle only applies when
                // visibility rules exist (and the UI hides it otherwise). Don't
                // persist a stale `true` once all rules are gone, or it would
                // silently re-arm if a rule is later re-added.
                showGloballyAfterEngagement: normalizedVisibilityRules.length > 0
                    ? showGloballyAfterEngagement
                    : false,
                widgetPosition: widgetPosition,
                disclaimerMessage: disclaimerMessage,
                privacyPolicyLink: privacyPolicyLink,
                collapsedMode: collapsedMode,
                expandedMode: expandedMode,
                voiceEnabled: voiceEnabled,
                selectedVoice: selectedVoice
                    ? { id: selectedVoice.id, name: selectedVoice.name }
                    : null,
                onboardingUrl: onboardingUrl,
                onboardingAllowlistUrls: normalizedOnboardingAllowlistUrls,
                onlyShowForAdmin: onlyShowForAdmin,
                darkModeEnabled: darkModeEnabled,
                darkModeDefault: darkModeDefault,
                darkModeColor: darkModeColor,
                // Only persist the `app` key when at least one override is set.
                // Keeps JSONB payloads small for the 99% case where customers
                // inherit the website configuration everywhere.
                ...(() => {
                    const pruned = pruneAppOverrides(appOverrides);
                    return Object.keys(pruned).length > 0 ? { app: pruned } : {};
                })(),
            },
            website: normalizedWebsite,
            expected_updated_at: agId ? draftUpdatedAt ?? undefined : undefined,
        };

        conLog(data);

        setSaving(true);
        try {
            if (agId) {
                const response = await put(`/agents/${agId}`, data);
                setDraftId(response.data.id);
                let latestDraftUpdatedAt = response.data.updated_at ?? null;
                setDraftUpdatedAt(latestDraftUpdatedAt);
                if (previewUrl === "" && fileChanged) {
                    const logoResponse = await deleteLogo(latestDraftUpdatedAt);
                    latestDraftUpdatedAt = logoResponse?.updated_at ?? latestDraftUpdatedAt;
                    setDraftUpdatedAt(latestDraftUpdatedAt);
                } else {
                    const logoResponse = await uploadLogo(agId, file, latestDraftUpdatedAt);
                    latestDraftUpdatedAt = logoResponse?.updated_at ?? latestDraftUpdatedAt;
                    setDraftUpdatedAt(latestDraftUpdatedAt);
                }
                if (iconPreviewUrl === "" && iconFileChanged) {
                    const iconResponse = await deleteIcon(latestDraftUpdatedAt);
                    latestDraftUpdatedAt = iconResponse?.updated_at ?? latestDraftUpdatedAt;
                    setDraftUpdatedAt(latestDraftUpdatedAt);
                } else {
                    const iconResponse = await uploadIcon(agId, iconFile, latestDraftUpdatedAt);
                    latestDraftUpdatedAt = iconResponse?.updated_at ?? latestDraftUpdatedAt;
                    setDraftUpdatedAt(latestDraftUpdatedAt);
                }

                if (!silent && toggleModal) {
                    toggleModal();
                }
                toast.success(`${agentName} successfully updated.`, toastConfig);
                // Non-blocking staleness report: @-mentions in the goal,
                // persona, or skills that no longer resolve (the referenced
                // field/skill/objective was deleted or renamed).
                for (const warning of response.data.mention_warnings || []) {
                    toast.warn(`${warning}. The AI will see only the raw identifier.`, toastConfig);
                }
                captureEvent('agent_updated', {
                    agent_id: agId,
                    agent_name: agentName,
                });
            } else {
                data.config.form = [
                    {
                        name: 'first_name',
                        label: 'First Name',
                        type: 'text',
                        required: true,
                    },
                    {
                        name: 'last_name',
                        label: 'Last Name',
                        type: 'text',
                        required: true,
                    },
                    {
                        name: 'email',
                        label: 'Email',
                        type: 'email',
                        required: true,
                    }
                ];
                const response = await post('/agents/', data);
                const draftResponse = await get(`/agents/${response.data.id}`);
                setDraftId(draftResponse.data.id);
                let latestDraftUpdatedAt = draftResponse.data.updated_at ?? null;
                setDraftUpdatedAt(latestDraftUpdatedAt);
                const logoResponse = await uploadLogo(response.data.id, file, latestDraftUpdatedAt);
                latestDraftUpdatedAt = logoResponse?.updated_at ?? latestDraftUpdatedAt;
                setDraftUpdatedAt(latestDraftUpdatedAt);
                const iconResponse = await uploadIcon(response.data.id, iconFile, latestDraftUpdatedAt);
                latestDraftUpdatedAt = iconResponse?.updated_at ?? latestDraftUpdatedAt;
                setDraftUpdatedAt(latestDraftUpdatedAt);

                if (!silent && toggleModal) {
                    toggleModal();
                }
                if (docId) {
                    setAgId(response.data.id);
                    setAgents((prev: any) => [...prev, response.data]);
                } else {
                    navigate(`/agents/${response.data.id}`);
                }

                toast.success(`${agentName} successfully created.`, toastConfig);
                captureEvent('agent_created', {
                    agent_id: response.data.id,
                    agent_name: agentName,
                });
            }

            setRefreshPreview(true);
        } catch (error: any) {
            console.error('Error saving agent:', error);
            const saveIssueContent = !silent ? getSaveIssueModalContent(error) : null;
            if (saveIssueContent) {
                setSaveIssueModalContent(saveIssueContent);
                setSaveIssueModalOpen(true);
                toast.error('Please fix the listed issues and try saving again.', toastConfig);
            } else {
                toast.error(
                    getApiErrorMessage(
                        error,
                        agId ? `${agentName} failed to update.` : `${agentName} failed to create.`
                    ),
                    toastConfig
                );
            }
            throw error;
        } finally {
            setSaving(false);
            setUserInput('');
        }
    };

    useEffect(() => {
        if (agId) {
            const fetchContent = async () => {
                setInitialLoading(true)
                try {
                    const response = await get(`/agents/${agId}`);
                    const data = response.data;
                    conLog(data)
                    setDraftId(data.id);
                    setDraftUpdatedAt(data.updated_at ?? null);
                    setAgentName(data.name);
                    setAgentDescription(data.description);
                    setGoal(data.goal);
                    setPersona(data.persona);
                    setSelectedFields(data.fields);
                    setActions(data.tools ? data.tools : {});
                    setLinks(data.links ? data.links : []);
                    setStarters(data.starters ? data.starters : []);
                    setPreviewUrl(data.logo_url ? data.logo_url : '');
                    setIconPreviewUrl(data.message_icon_url ? data.message_icon_url : '');
                    setColorScheme(data.config.scheme);
                    setFont(data.config.font);
                    setColor(data.config.color);
                    setEndColor(data.config.endColor);
                    setWelcomeMessage(data.config.welcomeMessage);
                    setAutoOpenEnabled(data.config.autoOpenEnabled ? data.config.autoOpenEnabled : false);
                    setAutoOpenSeconds(data.config.autoOpenSeconds ? data.config.autoOpenSeconds : 0);
                    setOpenOncePerSession(data.config.openOncePerSession ? data.config.openOncePerSession : false);
                    setPopupMessageEnabled(data.config.popupMessageEnabled ? data.config.popupMessageEnabled : false);
                    setPopupMessage(data.config.popupMessage ? data.config.popupMessage : '');
                    setProactiveEnabled(data.config.proactiveEnabled ? data.config.proactiveEnabled : false);
                    setProactiveMinInactivityHours(String(data.config.proactiveMinInactivityHours ?? 12));
                    setProactiveMaxUnanswered(String(data.config.proactiveMaxUnanswered ?? 3));
                    setProactiveEmailFallback(data.config.proactiveEmailFallback ? data.config.proactiveEmailFallback : false);
                    setProactiveInstructions(data.config.proactiveInstructions ? data.config.proactiveInstructions : '');
                    setProactiveVisitorsEnabled(data.config.proactiveVisitorsEnabled ? data.config.proactiveVisitorsEnabled : false);
                    setProactiveVisitorsMinInactivityHours(String(data.config.proactiveVisitorsMinInactivityHours ?? 12));
                    setProactiveVisitorsMaxUnanswered(String(data.config.proactiveVisitorsMaxUnanswered ?? 2));
                    setProactiveVisitorsDailyCap(String(data.config.proactiveVisitorsDailyCap ?? 50));
                    setProactiveVisitorsInstructions(data.config.proactiveVisitorsInstructions ? data.config.proactiveVisitorsInstructions : '');
                    setAgentIconType(data.config.agentMessageIconType ? data.config.agentMessageIconType : 'Icon');
                    setAgentIcon(data.config.agentMessageIcon ? data.config.agentMessageIcon : 'FireIcon');
                    setEnableFollowUps(data.config.enableFollowUps ? data.config.enableFollowUps : false);
                    setEnableVisitorLocation(data.config.enableVisitorLocation ? data.config.enableVisitorLocation : false);
                    setFollowUpInstructions(data.config.followUpInstructions ? data.config.followUpInstructions : '');
                    setHideAimdoc(data.config.hideAimdoc ? data.config.hideAimdoc : false);
                    setForm(data.config.form ? data.config.form : []);
                    setOutcomes(normalizeOutcomesConfig(data.config.outcomes));
                    setTrackNodes(
                        Array.isArray(data.config?.tracks?.nodes)
                            ? data.config.tracks.nodes
                            : []
                    );
                    setEnableTracks(data.config.enableTracks ?? false);
                    setSkills(Array.isArray(data.config?.skills) ? data.config.skills : []);
                    setEnableSkills(data.config.enableSkills ?? false);
                    setScoringCategories(data.config.scoringCategories ?? []);
                    setContextualPopupEnabled(data.config.contextualPopupEnabled ? data.config.contextualPopupEnabled : false);
                    setContextualStartersEnabled(data.config.contextualStartersEnabled ? data.config.contextualStartersEnabled : false);
                    setEnablePageAwareness(data.config.enablePageAwareness ? data.config.enablePageAwareness : false);
                    setSmartNavigationEnabled(data.config.smartNavigationEnabled ?? false);
                    setChatbotEnabled(data.config.chatbotEnabled !== undefined ? data.config.chatbotEnabled : true);
                    setMobileEnabled(data.config.mobileEnabled !== undefined ? data.config.mobileEnabled : true);
                    setSearchBarEnabled(data.config.searchBarEnabled ? data.config.searchBarEnabled : false);
                    setSearchBarPlaceholder(data.config.searchBarPlaceholder ? data.config.searchBarPlaceholder : '👋 Ask AI');
                    setQualifyVisitor(data.config.qualifyVisitor ?? false);
                    setEnableCopilot(data.config.enableCopilot ? data.config.enableCopilot : false);
                    setEscalationEnabled(data.config.escalationEnabled ?? false);
                    setEscalationButtons(data.config.escalationButtons ?? []);
                    setGdprEnabled(data.config.gdprEnabled ?? false);
                    setWorkEmailOnly(data.config.workEmailOnly ?? false);
                    setRequireContactInfo(data.config.requireContactInfo ?? false);
                    setMessagesBeforeContactInfo(data.config.messagesBeforeContactInfo ?? 0);
                    setHideContactUsButton(data.config.hideContactUsButton ?? false);
                    setRateLimitMessages(data.config.security?.rateLimitMessages ?? 20);
                    setRateLimitSeconds(data.config.security?.rateLimitSeconds ?? 240);
                    setRateLimitMessage(data.config.security?.rateLimitMessage ?? 'You have reached the message limit. Please wait before sending another message.');
                    setAgentGatewayEnabled(data.config.agentGateway?.enabled ?? false);
                    setAgentGatewayDailyCap(data.config.agentGateway?.dailyMessageCap ?? 500);
                    setAgentGatewaySites(Array.isArray(data.config.agentGateway?.sites) ? data.config.agentGateway.sites : []);
                    setVisibilityRules(data.config.visibilityRules ?? []);
                    setContextualExclusionRules(Array.isArray(data.config.contextualExclusionRules) ? data.config.contextualExclusionRules : []);
                    setShowGloballyAfterEngagement(Boolean(
                        data.config.showGloballyAfterEngagement
                        ?? data.config.show_globally_after_engagement
                        ?? false
                    ));
                    setWidgetPosition(data.config.widgetPosition ?? 'right');
                    setDisclaimerMessage(data.config.disclaimerMessage ?? '');
                    setPrivacyPolicyLink(data.config.privacyPolicyLink ?? '');
                    // Load new composable fields, migrating from old format if needed
                    if (data.config.collapsedMode && data.config.expandedMode) {
                        setCollapsedMode(data.config.collapsedMode);
                        setExpandedMode(data.config.expandedMode);
                    } else {
                        const oldPresentation = data.config.defaultPresentation ?? 'chatbot';
                        if (oldPresentation === 'copilot') {
                            setCollapsedMode('copilot');
                            setExpandedMode('copilot');
                        } else if (oldPresentation === 'sidebar') {
                            setCollapsedMode('chat');
                            setExpandedMode('sidebar');
                        } else {
                            setCollapsedMode('chat');
                            setExpandedMode('chat');
                        }
                    }
                    setVoiceEnabled(data.config.voiceEnabled ?? false);
                    setOnboardingUrl(data.config.onboardingUrl ?? '');
                    const rawAllowlistUrls = data.config.onboardingAllowlistUrls ?? data.config.onboarding_allowlist_urls ?? [];
                    const normalizedAllowlistUrls = Array.isArray(rawAllowlistUrls)
                        ? rawAllowlistUrls
                            .filter((url: unknown): url is string => typeof url === 'string')
                            .map((url: string) => url.trim())
                            .filter((url: string) => url.length > 0)
                        : [];
                    setOnboardingAllowlistUrls(normalizedAllowlistUrls);
                    setOnlyShowForAdmin(Boolean(data.config.onlyShowForAdmin ?? data.config.only_show_for_admin ?? false));
                    setAppOverrides(normalizeAppOverrides(data.config?.app));
                    setDarkModeEnabled(Boolean(data.config.darkModeEnabled ?? data.config.dark_mode_enabled ?? false));
                    const rawDefault = data.config.darkModeDefault ?? data.config.dark_mode_default ?? 'system';
                    setDarkModeDefault(['system', 'light', 'dark'].includes(rawDefault) ? rawDefault : 'system');
                    setDarkModeColor(data.config.darkModeColor ?? data.config.dark_mode_color ?? '#111111');
                    const storedVoice = data.config.selectedVoice;
                    if (storedVoice) {
                        if (typeof storedVoice === 'string') {
                            setSelectedVoice({ id: storedVoice, name: storedVoice });
                        } else {
                            setSelectedVoice({ id: storedVoice.id, name: storedVoice.name || storedVoice.id });
                        }
                    } else {
                        setSelectedVoice(null);
                    }
                } catch (error) {
                    console.error('Error fetching content:', error);
                }
                setInitialLoading(false);
            };
            fetchContent();
        }
    }, [agId]);

    // Send operator agent message immediately when arriving from onboarding
    useEffect(() => {
        if (!agId || onboardingMessageSentRef.current) return;
        if (location.state?.fromOnboarding) {
            onboardingMessageSentRef.current = true;
            const timerId = setTimeout(() => {
                sendMessage(
                    "The user's AI agent just finished training on their website. Search for the onboarding task and start it.",
                    { agentId: import.meta.env.VITE_PUBLIC_AGENT_ID }
                );
            }, 2000);
            return () => clearTimeout(timerId);
        }
    }, [agId, location.state]);

    useEffect(() => {
        if (!agId && id) {
            setSelectedFields(fields);
        }
    }, []);

    useEffect(() => {
        if (needsUpdate) {
            createAgent(true).catch(() => undefined);
            setNeedsUpdate(false);
        };
    }, [needsUpdate]);

    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file]);

    async function uploadLogo(id: string, file: any, expectedUpdatedAt?: string | null) {
        const formData = new FormData();
        if (file && fileChanged) {
            formData.append('file', file);
            if (expectedUpdatedAt) {
                formData.append('expected_updated_at', expectedUpdatedAt);
            }
            try {
                const response = await post(`/agents/${id}/logo`, formData);
                conLog('Success');
                setFileChanged(false);
                return response.data;
            } catch (error) {
                console.error('Error uploading logo:', error);
                throw error;
            }
        }
    }

    const deleteLogo = async (expectedUpdatedAt?: string | null) => {
        if (!agId) return;
        try {
            const query = expectedUpdatedAt ? `?expected_updated_at=${encodeURIComponent(expectedUpdatedAt)}` : '';
            const response = await remove(`/agents/${agId}/logo${query}`);
            setFile(null);
            setPreviewUrl('');
            setFileChanged(false);
            return response.data;
        } catch (error) {
            console.error('Error deleting logo:', error);
            throw error;
        }
    };

    useEffect(() => {
        if (iconFile) {
            const url = URL.createObjectURL(iconFile);
            setIconPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [iconFile]);

    async function uploadIcon(id: string, file: any, expectedUpdatedAt?: string | null) {
        const formData = new FormData();
        if (iconFile && iconFileChanged) {
            formData.append('file', file);
            if (expectedUpdatedAt) {
                formData.append('expected_updated_at', expectedUpdatedAt);
            }
            try {
                const response = await post(`/agents/${id}/message-icon`, formData);
                conLog('Success');
                setIconFileChanged(false);
                return response.data;
            } catch (error) {
                console.error('Error uploading message icon:', error);
                throw error;
            }
        }
    }

    const deleteIcon = async (expectedUpdatedAt?: string | null) => {
        if (!agId) return;
        try {
            const query = expectedUpdatedAt ? `?expected_updated_at=${encodeURIComponent(expectedUpdatedAt)}` : '';
            const response = await remove(`/agents/${agId}/message-icon${query}`);
            setIconFile(null);
            setIconPreviewUrl('');
            setIconFileChanged(false);
            return response.data;
        } catch (error) {
            console.error('Error deleting message icon:', error);
            throw error;
        }
    };


    function mixColors(color1: string, color2: string, weight = 0.5) {
        let [r1, g1, b1] = hexToRgb(color1);
        let [r2, g2, b2] = hexToRgb(color2);

        let r = Math.round(r1 * (1 - weight) + r2 * weight);
        let g = Math.round(g1 * (1 - weight) + g2 * weight);
        let b = Math.round(b1 * (1 - weight) + b2 * weight);

        return rgbToHex(r, g, b);
    }

    function hexToRgb(hex: any) {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b];
    }

    function rgbToHex(r: number, g: number, b: number) {
        return "#" + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    }

    function luminance(r: number, g: number, b: number) {
        var a = [r, g, b].map(function (v) {
            v /= 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    }

    function compareColors(hex1: string, hex2: string) {
        var rgb1 = hexToRgb(hex1);
        var rgb2 = hexToRgb(hex2);
        var lum1 = luminance(rgb1[0], rgb1[1], rgb1[2]);
        var lum2 = luminance(rgb2[0], rgb2[1], rgb2[2]);

        if (lum1 < lum2) {
            return { darkerColor: hex1, lighterColor: hex2 };
        } else {
            return { darkerColor: hex2, lighterColor: hex1 };
        }
    }

    if (initialLoading) {
        return <AimdocLoader label="Loading agent configuration" />
    }

    return (
        <div className="h-screen w-full flex flex-col border-r">
            {agId && agentName === "" ? <Spinner /> : (
                <div className="h-full w-full flex flex-col overflow-hidden">                    
                    <div className="flex items-center justify-between pt-4 pl-4 pr-4 shrink-0">
                        <div className="flex items-center space-x-4">
                            <Button
                                onClick={() => navigate('/agents')}
                                variant="ghost"
                                className="flex items-center gap-2"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Back to Agents
                            </Button>
                            <Separator orientation="vertical" className="h-6" />
                            {agId && (
                                <div className="flex items-center gap-2">
                                    <h2 className="text-sm font-bold">{agentName}</h2>
                                </div>
                            )}
                        </div>
                        {agId && (
                            <AgentEditorHeaderActions
                                agId={agId}
                                draftUpdatedAt={draftUpdatedAt}
                                onSaveBeforePublish={() => createAgent(true)}
                                onSaveDraft={() => createAgent().catch(() => undefined)}
                                isSavingDraft={saving}
                                onDraftVersionResync={(updatedAt) => setDraftUpdatedAt(updatedAt)}
                            />
                        )}
                    </div>

                    {!agId && (
                        <div className="p-4 shrink-0">
                            <Card className="mb-6">
                                <CardHeader>
                                    <CardTitle>Create New Agent</CardTitle>
                                    <CardDescription>
                                        Configure your new AI agent by providing basic information
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Agent Name</Label>
                                            <Input
                                                id="name"
                                                placeholder="Enter your agent name..."
                                                value={agentName}
                                                onChange={(e) => setAgentName(e.target.value)}
                                                className="max-w-md"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                    <div ref={builderLayoutRef} className="flex min-w-0 flex-1 overflow-hidden p-4">
                        <Tabs 
                            value={selectedTab || 'instructions'} 
                            onValueChange={handleTabChange} 
                            orientation="vertical"
                            className="flex h-full min-w-0 flex-1 items-start"
                        >
                            <TabsList className="flex h-full w-56 mr-4 flex-col items-stretch gap-1 p-2 border shrink-0 overflow-y-auto scrollbar-hidden justify-start relative">
                                {TAB_SECTIONS.map((section, sectionIndex) => {
                                    const hasLicense = section.licenseGated
                                        ? checkLicenseType(section.licenseGated)
                                        : true;
                                    const modeSectionId = section.id === 'shared' ? null : section.id;
                                    const isExpanded = modeSectionId
                                        ? expandedModeSections[modeSectionId]
                                        : true;
                                    return (
                                        <div key={section.id} className="w-full">
                                            {sectionIndex > 0 && (
                                                <hr className="my-1.5 border-t border-border/60" />
                                            )}
                                            {section.label && (
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted/60"
                                                    aria-expanded={isExpanded}
                                                    onClick={() => {
                                                        if (modeSectionId) {
                                                            setExpandedModeSections((current) => ({
                                                                ...current,
                                                                [modeSectionId]: !current[modeSectionId],
                                                            }));
                                                        }
                                                    }}
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown className="h-3 w-3" />
                                                    ) : (
                                                        <ChevronRight className="h-3 w-3" />
                                                    )}
                                                    <span className="flex-1 text-left">{section.label}</span>
                                                    {section.licenseGated && !hasLicense && (
                                                        <Lock className="w-3 h-3 text-muted-foreground/70" />
                                                    )}
                                                </button>
                                            )}
                                            {isExpanded && section.tabs.filter((tab) => !tab.hidden).map((tab) => {
                                                if (tab.accordion && tab.name === 'knowledge') {
                                                    return (
                                                        <div key="knowledge" className="w-full relative">
                                                            <Accordion
                                                                type="single"
                                                                collapsible
                                                                value={accordionValue}
                                                                onValueChange={handleAccordionChange}
                                                            >
                                                                <AccordionItem value="knowledge" className="border-0">
                                                                    <AccordionTrigger className="px-3 py-1 hover:no-underline rounded-md transition-all relative">
                                                                        <div className="flex items-center gap-2">
                                                                            {tab.icon}
                                                                            <span>{tab.label}</span>
                                                                        </div>
                                                                    </AccordionTrigger>
                                                                    <AccordionContent className="pt-1 pb-0 relative">
                                                                        <div className="relative pl-7">
                                                                            <div className="flex flex-col gap-1">
                                                                                <div className="relative">
                                                                                    <div
                                                                                        className={cn(
                                                                                            'absolute left-[-9px] bottom-0 transition-colors',
                                                                                            selectedTab === 'knowledge:websites'
                                                                                                ? 'bg-primary'
                                                                                                : 'bg-border/50'
                                                                                        )}
                                                                                        style={{
                                                                                            height: 'calc(100% + 4px)',
                                                                                            width: '1px',
                                                                                        }}
                                                                                    />
                                                                                    <TabsTrigger
                                                                                        value="knowledge:websites"
                                                                                        className="flex w-full items-center justify-start gap-2"
                                                                                    >
                                                                                        <Globe className="w-4 h-4" />
                                                                                        Websites
                                                                                    </TabsTrigger>
                                                                                </div>
                                                                                <div className="relative">
                                                                                    <div
                                                                                        className={cn(
                                                                                            'absolute left-[-9px] bottom-0 transition-colors',
                                                                                            selectedTab === 'knowledge:documents'
                                                                                                ? 'bg-primary'
                                                                                                : 'bg-border/50'
                                                                                        )}
                                                                                        style={{
                                                                                            height: 'calc(100% + 4px)',
                                                                                            width: '1px',
                                                                                        }}
                                                                                    />
                                                                                    <TabsTrigger
                                                                                        value="knowledge:documents"
                                                                                        className="flex w-full items-center justify-start gap-2"
                                                                                    >
                                                                                        <FileText className="w-4 h-4" />
                                                                                        Documents
                                                                                    </TabsTrigger>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </AccordionContent>
                                                                </AccordionItem>
                                                            </Accordion>
                                                        </div>
                                                    );
                                                }

                                                const overrideCount = tab.overrideGroup
                                                    ? countOverrides(
                                                          appOverrides,
                                                          tab.overrideGroup === 'prompting'
                                                              ? PROMPTING_OVERRIDE_KEYS
                                                              : tab.overrideGroup === 'customization'
                                                                ? CUSTOMIZATION_OVERRIDE_KEYS
                                                                : APPEARANCE_OVERRIDE_KEYS
                                                      )
                                                    : 0;

                                                if (section.licenseGated && !hasLicense) {
                                                    return (
                                                        <TooltipProvider key={tab.name}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="flex w-full items-center justify-start gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground/50 cursor-not-allowed">
                                                                        <Lock className="w-4 h-4" />
                                                                        <span>{tab.label}</span>
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Activate plan required</TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    );
                                                }

                                                return (
                                                    <TabsTrigger
                                                        key={tab.name}
                                                        value={tab.name}
                                                        className="flex w-full items-center justify-start gap-2"
                                                    >
                                                        {tab.icon}
                                                        <span className="flex-1 text-left">{tab.label}</span>
                                                        {overrideCount > 0 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="h-4 px-1.5 text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                                                            >
                                                                {overrideCount}
                                                            </Badge>
                                                        )}
                                                    </TabsTrigger>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </TabsList>

                            <div className="mr-4 h-full min-w-0 flex-1 overflow-hidden rounded-lg border">
                                <div className="h-full w-full overflow-y-auto">
                                    <div className="@container w-full min-w-0 max-w-full p-4">
                                        <TabsContent value="instructions">
                                            <InstructionsTab
                                                            agId={agId}
                                                            agentName={agentName}
                                                            setAgentName={setAgentName}
                                                            form={form}
                                                            skills={skills}
                                                            goal={goal}
                                                            setGoal={setGoal}
                                                            persona={persona}
                                                            setPersona={setPersona}
                                                            setNeedsUpdate={setNeedsUpdate}
                                            />
                                        </TabsContent>
                                        <TabsContent value="data_capture">
                                            <DataCaptureTab
                                                agId={agId}
                                                agentName={agentName}
                                                form={form}
                                                setForm={setForm}
                                                outcomes={outcomes}
                                                setOutcomes={setOutcomes}
                                                enableTracks={enableTracks}
                                                trackNodes={trackNodes}
                                                setTrackNodes={setTrackNodes}
                                                actions={actions}
                                                setActions={setActions}
                                                qualifyVisitor={qualifyVisitor}
                                                setQualifyVisitor={setQualifyVisitor}
                                                setNeedsUpdate={setNeedsUpdate}
                                                workEmailOnly={workEmailOnly}
                                                setWorkEmailOnly={setWorkEmailOnly}
                                                requireContactInfo={requireContactInfo}
                                                setRequireContactInfo={setRequireContactInfo}
                                                messagesBeforeContactInfo={messagesBeforeContactInfo}
                                                setMessagesBeforeContactInfo={setMessagesBeforeContactInfo}
                                                hideContactUsButton={hideContactUsButton}
                                                setHideContactUsButton={setHideContactUsButton}
                                            />
                                        </TabsContent>
                                        <TabsContent value="skills">
                                            <SkillsTab
                                                agId={agId}
                                                agentName={agentName}
                                                form={form}
                                                skills={skills}
                                                setSkills={setSkills}
                                                enableSkills={enableSkills}
                                                setEnableSkills={setEnableSkills}
                                                enableTracks={enableTracks}
                                                actions={actions}
                                                setNeedsUpdate={setNeedsUpdate}
                                            />
                                        </TabsContent>
                                        <TabsContent value="objectives">
                                            {agId && <ObjectivesTab agId={agId} form={form} skills={skills} />}
                                        </TabsContent>
                                        <TabsContent value="workflow">
                                            <WorkflowTab
                                                agId={agId}
                                                agentName={agentName}
                                                goal={goal}
                                                persona={persona}
                                                form={form}
                                                trackNodes={trackNodes}
                                                setTrackNodes={setTrackNodes}
                                                enableTracks={enableTracks}
                                                setEnableTracks={setEnableTracks}
                                                enableSkills={enableSkills}
                                                actions={actions}
                                                setNeedsUpdate={setNeedsUpdate}
                                            />
                                        </TabsContent>
                                        <TabsContent value="knowledge:websites">
                                            <div className="p-0">
                                                <Website
                                                    agentId={agId}
                                                    agentName={agentName}
                                                    website={website}
                                                    selectedPages={selectedPages}
                                                    setSelectedPages={setSelectedPages}
                                                    setWebsite={setWebsite}
                                                    indexedPages={indexedPages}
                                                    setIndexedPages={setIndexedPages}
                                                />
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="knowledge:documents">
                                            <div className="p-0">
                                                {agId && (
                                                    <Files agentId={agId} agentName={agentName} />
                                                )}
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="appearance">
                                            <AppearanceTab
                                                            agId={agId}
                                                            agentName={agentName}
                                                            color={color}
                                                            setColor={setColor}
                                                            endColor={endColor}
                                                            setEndColor={setEndColor}
                                                            colorScheme={colorScheme}
                                                            setColorScheme={setColorScheme}
                                                            font={font}
                                                            setFont={setFont}
                                                            welcomeMessage={welcomeMessage}
                                                            setWelcomeMessage={setWelcomeMessage}
                                                            links={links}
                                                            setLinks={setLinks}
                                                            starters={starters}
                                                            setStarters={setStarters}
                                                            autoOpenEnabled={autoOpenEnabled}
                                                            setAutoOpenEnabled={setAutoOpenEnabled}
                                                            enableVisitorLocation={enableVisitorLocation}
                                                            setEnableVisitorLocation={setEnableVisitorLocation}
                                                            enablePageAwareness={enablePageAwareness}
                                                            setEnablePageAwareness={setEnablePageAwareness}
                                                            smartNavigationEnabled={smartNavigationEnabled}
                                                            setSmartNavigationEnabled={setSmartNavigationEnabled}
                                                            openOncePerSession={openOncePerSession}
                                                            setOpenOncePerSession={setOpenOncePerSession}
                                                            autoOpenSeconds={autoOpenSeconds}
                                                            setAutoOpenSeconds={setAutoOpenSeconds}
                                                            popupMessageEnabled={popupMessageEnabled}
                                                            setPopupMessageEnabled={setPopupMessageEnabled}
                                                            popupMessage={popupMessage}
                                                            setPopupMessage={setPopupMessage}
                                                            contextualPopupEnabled={contextualPopupEnabled}
                                                            setContextualPopupEnabled={setContextualPopupEnabled}
                                                            contextualStartersEnabled={contextualStartersEnabled}
                                                            setContextualStartersEnabled={setContextualStartersEnabled}
                                                            enableFollowUps={enableFollowUps}
                                                            setEnableFollowUps={setEnableFollowUps}
                                                            followUpInstructions={followUpInstructions}
                                                            setFollowUpInstructions={setFollowUpInstructions}
                                                            file={file}
                                                            setFile={setFile}
                                                            previewUrl={previewUrl}
                                                            setPreviewUrl={setPreviewUrl}
                                                            iconFile={iconFile}
                                                            setIconFile={setIconFile}
                                                            iconPreviewUrl={iconPreviewUrl}
                                                            setIconPreviewUrl={setIconPreviewUrl}
                                                            agentIconType={agentIconType}
                                                            setAgentIconType={setAgentIconType}
                                                            agentIcon={agentIcon}
                                                            setAgentIcon={setAgentIcon}
                                                            setNeedsUpdate={setNeedsUpdate}
                                                            setFileChanged={setFileChanged}
                                                            setIconFileChanged={setIconFileChanged}
                                                            escalationEnabled={escalationEnabled}
                                                            setEscalationEnabled={setEscalationEnabled}
                                                            escalationButtons={escalationButtons}
                                                            setEscalationButtons={setEscalationButtons}
                                                            gdprEnabled={gdprEnabled}
                                                            setGdprEnabled={setGdprEnabled}
                                                            searchBarEnabled={searchBarEnabled}
                                                            setSearchBarEnabled={setSearchBarEnabled}
                                                            searchBarPlaceholder={searchBarPlaceholder}
                                                            setSearchBarPlaceholder={setSearchBarPlaceholder}
                                                            hideAimdoc={hideAimdoc} 
                                                            setHideAimdoc={setHideAimdoc}
                                                            rateLimitMessages={rateLimitMessages}
                                                            setRateLimitMessages={setRateLimitMessages}
                                                            rateLimitSeconds={rateLimitSeconds}
                                                            setRateLimitSeconds={setRateLimitSeconds}
                                                            rateLimitMessage={rateLimitMessage}
                                                            setRateLimitMessage={setRateLimitMessage}
                                                            agentGatewayEnabled={agentGatewayEnabled}
                                                            setAgentGatewayEnabled={setAgentGatewayEnabled}
                                                            agentGatewayDailyCap={agentGatewayDailyCap}
                                                            setAgentGatewayDailyCap={setAgentGatewayDailyCap}
                                                            visibilityRules={visibilityRules}
                                                            setVisibilityRules={setVisibilityRules}
                                                            contextualExclusionRules={contextualExclusionRules}
                                                            setContextualExclusionRules={setContextualExclusionRules}
                                                            showGloballyAfterEngagement={showGloballyAfterEngagement}
                                                            setShowGloballyAfterEngagement={setShowGloballyAfterEngagement}
                                                            widgetPosition={widgetPosition}
                                                            setWidgetPosition={setWidgetPosition}
                                                            collapsedMode={collapsedMode}
                                                            setCollapsedMode={setCollapsedMode}
                                                            expandedMode={expandedMode}
                                                            setExpandedMode={setExpandedMode}
                                                            darkModeEnabled={darkModeEnabled}
                                                            setDarkModeEnabled={setDarkModeEnabled}
                                                            darkModeDefault={darkModeDefault}
                                                            setDarkModeDefault={setDarkModeDefault}
                                                            darkModeColor={darkModeColor}
                                                            setDarkModeColor={setDarkModeColor}
                                            />
                                        </TabsContent>

                                        <TabsContent value="voice">
                                            <VoiceTab
                                                agId={agId}
                                                agentName={agentName}
                                                voiceEnabled={voiceEnabled}
                                                setVoiceEnabled={setVoiceEnabled}
                                                selectedVoice={selectedVoice}
                                                setSelectedVoice={setSelectedVoice}
                                                setNeedsUpdate={setNeedsUpdate}
                                            />
                                        </TabsContent>

                                        <TabsContent value="customization">
                                            <CustomizationTab
                                                            agId={agId}
                                                            agentName={agentName}
                                                            color={color}
                                                            setColor={setColor}
                                                            endColor={endColor}
                                                            setEndColor={setEndColor}
                                                            colorScheme={colorScheme}
                                                            setColorScheme={setColorScheme}
                                                            font={font}
                                                            setFont={setFont}
                                                            welcomeMessage={welcomeMessage}
                                                            setWelcomeMessage={setWelcomeMessage}
                                                            links={links}
                                                            setLinks={setLinks}
                                                            starters={starters}
                                                            setStarters={setStarters}
                                                            autoOpenEnabled={autoOpenEnabled}
                                                            setAutoOpenEnabled={setAutoOpenEnabled}
                                                            autoOpenSeconds={autoOpenSeconds}
                                                            setAutoOpenSeconds={setAutoOpenSeconds}
                                                            enableVisitorLocation={enableVisitorLocation}
                                                            setEnableVisitorLocation={setEnableVisitorLocation}
                                                            enablePageAwareness={enablePageAwareness}
                                                            setEnablePageAwareness={setEnablePageAwareness}
                                                            smartNavigationEnabled={smartNavigationEnabled}
                                                            setSmartNavigationEnabled={setSmartNavigationEnabled}
                                                            openOncePerSession={openOncePerSession}
                                                            setOpenOncePerSession={setOpenOncePerSession}
                                                            popupMessageEnabled={popupMessageEnabled}
                                                            setPopupMessageEnabled={setPopupMessageEnabled}
                                                            popupMessage={popupMessage}
                                                            setPopupMessage={setPopupMessage}
                                                            contextualPopupEnabled={contextualPopupEnabled}
                                                            setContextualPopupEnabled={setContextualPopupEnabled}
                                                            contextualStartersEnabled={contextualStartersEnabled}
                                                            setContextualStartersEnabled={setContextualStartersEnabled}
                                                            enableFollowUps={enableFollowUps}
                                                            setEnableFollowUps={setEnableFollowUps}
                                                            followUpInstructions={followUpInstructions}
                                                            setFollowUpInstructions={setFollowUpInstructions}
                                                            file={file}
                                                            setFile={setFile}
                                                            previewUrl={previewUrl}
                                                            setPreviewUrl={setPreviewUrl}
                                                            iconFile={iconFile}
                                                            setIconFile={setIconFile}
                                                            iconPreviewUrl={iconPreviewUrl}
                                                            setIconPreviewUrl={setIconPreviewUrl}
                                                            agentIconType={agentIconType}
                                                            setAgentIconType={setAgentIconType}
                                                            agentIcon={agentIcon}
                                                            setAgentIcon={setAgentIcon}
                                                            setNeedsUpdate={setNeedsUpdate}
                                                            setFileChanged={setFileChanged}
                                                            setIconFileChanged={setIconFileChanged}
                                                            escalationEnabled={escalationEnabled}
                                                            setEscalationEnabled={setEscalationEnabled}
                                                            escalationButtons={escalationButtons}
                                                            setEscalationButtons={setEscalationButtons}
                                                            gdprEnabled={gdprEnabled}
                                                            setGdprEnabled={setGdprEnabled}
                                                            searchBarEnabled={searchBarEnabled}
                                                            setSearchBarEnabled={setSearchBarEnabled}
                                                            searchBarPlaceholder={searchBarPlaceholder}
                                                            setSearchBarPlaceholder={setSearchBarPlaceholder}
                                                            hideAimdoc={hideAimdoc} 
                                                            setHideAimdoc={setHideAimdoc}
                                                            rateLimitMessages={rateLimitMessages}
                                                            setRateLimitMessages={setRateLimitMessages}
                                                            rateLimitSeconds={rateLimitSeconds}
                                                            setRateLimitSeconds={setRateLimitSeconds}
                                                            rateLimitMessage={rateLimitMessage}
                                                            setRateLimitMessage={setRateLimitMessage}
                                                            agentGatewayEnabled={agentGatewayEnabled}
                                                            setAgentGatewayEnabled={setAgentGatewayEnabled}
                                                            agentGatewayDailyCap={agentGatewayDailyCap}
                                                            setAgentGatewayDailyCap={setAgentGatewayDailyCap}
                                                            visibilityRules={visibilityRules}
                                                            setVisibilityRules={setVisibilityRules}
                                                            contextualExclusionRules={contextualExclusionRules}
                                                            setContextualExclusionRules={setContextualExclusionRules}
                                                            showGloballyAfterEngagement={showGloballyAfterEngagement}
                                                            setShowGloballyAfterEngagement={setShowGloballyAfterEngagement}
                                                            widgetPosition={widgetPosition}
                                                            setWidgetPosition={setWidgetPosition}
                                                            disclaimerMessage={disclaimerMessage}
                                                            setDisclaimerMessage={setDisclaimerMessage}
                                                            privacyPolicyLink={privacyPolicyLink}
                                                            setPrivacyPolicyLink={setPrivacyPolicyLink}
                                                            darkModeEnabled={darkModeEnabled}
                                                            setDarkModeEnabled={setDarkModeEnabled}
                                                            darkModeDefault={darkModeDefault}
                                                            setDarkModeDefault={setDarkModeDefault}
                                                            darkModeColor={darkModeColor}
                                                            setDarkModeColor={setDarkModeColor}
                                            />
                                        </TabsContent>

                                        <TabsContent value="agent_gateway">
                                            <AgentGatewayTab
                                                agId={agId}
                                                agentName={agentName}
                                                setNeedsUpdate={setNeedsUpdate}
                                                agentGatewayEnabled={agentGatewayEnabled}
                                                setAgentGatewayEnabled={setAgentGatewayEnabled}
                                                agentGatewayDailyCap={agentGatewayDailyCap}
                                                setAgentGatewayDailyCap={setAgentGatewayDailyCap}
                                                agentGatewaySites={agentGatewaySites}
                                                setAgentGatewaySites={setAgentGatewaySites}
                                            />
                                        </TabsContent>

                                        <TabsContent value="email">
                                            <EmailChannelTab
                                                agId={agId}
                                                agentName={agentName}
                                            />
                                        </TabsContent>

                                        <TabsContent value="proactive">
                                            <ProactiveTab
                                                agId={agId}
                                                agentName={agentName}
                                                setNeedsUpdate={setNeedsUpdate}
                                                proactiveEnabled={proactiveEnabled}
                                                setProactiveEnabled={setProactiveEnabled}
                                                proactiveMinInactivityHours={proactiveMinInactivityHours}
                                                setProactiveMinInactivityHours={setProactiveMinInactivityHours}
                                                proactiveMaxUnanswered={proactiveMaxUnanswered}
                                                setProactiveMaxUnanswered={setProactiveMaxUnanswered}
                                                proactiveEmailFallback={proactiveEmailFallback}
                                                setProactiveEmailFallback={setProactiveEmailFallback}
                                                proactiveInstructions={proactiveInstructions}
                                                setProactiveInstructions={setProactiveInstructions}
                                                proactiveVisitorsEnabled={proactiveVisitorsEnabled}
                                                setProactiveVisitorsEnabled={setProactiveVisitorsEnabled}
                                                proactiveVisitorsMinInactivityHours={proactiveVisitorsMinInactivityHours}
                                                setProactiveVisitorsMinInactivityHours={setProactiveVisitorsMinInactivityHours}
                                                proactiveVisitorsMaxUnanswered={proactiveVisitorsMaxUnanswered}
                                                setProactiveVisitorsMaxUnanswered={setProactiveVisitorsMaxUnanswered}
                                                proactiveVisitorsDailyCap={proactiveVisitorsDailyCap}
                                                setProactiveVisitorsDailyCap={setProactiveVisitorsDailyCap}
                                                proactiveVisitorsInstructions={proactiveVisitorsInstructions}
                                                setProactiveVisitorsInstructions={setProactiveVisitorsInstructions}
                                                form={form}
                                                skills={skills}
                                            />
                                        </TabsContent>

                                        <TabsContent value="actions">
                                            <ActionsTab
                                                            agId={agId}
                                                            agentName={agentName}
                                                            actions={actions}
                                                            setActions={setActions}
                                                            form={form}
                                                            setNeedsUpdate={setNeedsUpdate}
                                                            escalationEnabled={escalationEnabled}
                                                            enableTracks={enableTracks}
                                            />
                                        </TabsContent>
                                        <TabsContent value="mcp">
                                            <McpServersTab
                                                            agId={agId}
                                                            agentName={agentName}
                                                            actions={actions}
                                                            setActions={setActions}
                                                            setNeedsUpdate={setNeedsUpdate}
                                            />
                                        </TabsContent>

                                        {/* ---------- In-app tabs ---------- */}
                                        <TabsContent value="app_prompting">
                                            {!checkLicenseType('activate') ? (
                                                <InAppLicenseGate onUpgrade={() => setUpgradeModalVisible(true)} />
                                            ) : !hasInAppUrls ? (
                                                <InAppUrlsGate onGoToConfig={() => handleTabChange('app_configuration')} />
                                            ) : (
                                                <AppPromptingTab
                                                    agId={agId}
                                                    appOverrides={appOverrides}
                                                    setAppOverrides={setAppOverrides}
                                                    form={form}
                                                    skills={skills}
                                                />
                                            )}
                                        </TabsContent>
                                        <TabsContent value="app_configuration">
                                            {!checkLicenseType('activate') ? (
                                                <InAppLicenseGate onUpgrade={() => setUpgradeModalVisible(true)} />
                                            ) : (
                                                <AppConfigurationTab
                                                    agId={agId}
                                                    agentName={agentName}
                                                    setNeedsUpdate={setNeedsUpdate}
                                                    appOverrides={appOverrides}
                                                    setAppOverrides={setAppOverrides}
                                                    baseWelcomeMessage={welcomeMessage}
                                                    baseStarters={starters}
                                                    baseLinks={links}
                                                    baseSearchBarPlaceholder={searchBarPlaceholder}
                                                    basePopupMessage={popupMessage}
                                                    basePopupMessageEnabled={popupMessageEnabled}
                                                    baseContextualPopupEnabled={contextualPopupEnabled}
                                                    baseAutoOpenEnabled={autoOpenEnabled}
                                                    baseAutoOpenSeconds={autoOpenSeconds}
                                                    baseOpenOncePerSession={openOncePerSession}
                                                    onboardingUrl={onboardingUrl}
                                                    setOnboardingUrl={setOnboardingUrl}
                                                    onboardingAllowlistUrls={onboardingAllowlistUrls}
                                                    setOnboardingAllowlistUrls={setOnboardingAllowlistUrls}
                                                    onlyShowForAdmin={onlyShowForAdmin}
                                                    setOnlyShowForAdmin={setOnlyShowForAdmin}
                                                />
                                            )}
                                        </TabsContent>
                                        <TabsContent value="app_appearance">
                                            {!checkLicenseType('activate') ? (
                                                <InAppLicenseGate onUpgrade={() => setUpgradeModalVisible(true)} />
                                            ) : !hasInAppUrls ? (
                                                <InAppUrlsGate onGoToConfig={() => handleTabChange('app_configuration')} />
                                            ) : (
                                                <AppAppearanceTab
                                                    appOverrides={appOverrides}
                                                    setAppOverrides={setAppOverrides}
                                                    baseColor={color}
                                                    baseEndColor={endColor}
                                                    baseColorScheme={colorScheme}
                                                    baseFont={font}
                                                    baseWidgetPosition={widgetPosition}
                                                    baseHideAimdoc={hideAimdoc}
                                                    baseDarkModeEnabled={darkModeEnabled}
                                                    baseDarkModeDefault={darkModeDefault}
                                                    baseDarkModeColor={darkModeColor}
                                                />
                                            )}
                                        </TabsContent>
                                        <TabsContent value="app_tasks">
                                            {!checkLicenseType('activate') ? (
                                                <InAppLicenseGate onUpgrade={() => setUpgradeModalVisible(true)} />
                                            ) : (
                                                <TaskDefinitionsTab
                                                    agId={agId}
                                                    agentName={agentName}
                                                />
                                            )}
                                        </TabsContent>

                                        {agId && (
                                            <TabsContent value="deploy">
                                                <div className="flex flex-col w-full">
                                                    <DeployAgent
                                                        agId={agId}
                                                        agentName={agentName}
                                                        chatbotEnabled={chatbotEnabled}
                                                        setChatbotEnabled={setChatbotEnabled}
                                                        mobileEnabled={mobileEnabled}
                                                        setMobileEnabled={setMobileEnabled}
                                                        setNeedsUpdate={setNeedsUpdate}
                                                    />
                                                </div>
                                        </TabsContent>
                                    )}
                                    </div>
                                </div>
                            </div>
                        </Tabs>
                        
                        {agId && showPreview && (
                            <PreviewAgent 
                                agId={agId} 
                                draftId={draftId} 
                                refresh={refreshPreview} 
                                setRefreshPreview={setRefreshPreview}
                            />
                        )}
                    </div>
                </div>
            )}
            <Upgrade showModal={upgradeModalVisible} setShowModal={setUpgradeModalVisible} sessionId={null} />
            <Dialog open={saveIssueModalOpen} onOpenChange={setSaveIssueModalOpen}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{saveIssueModalContent?.title || "Can't save yet"}</DialogTitle>
                        <DialogDescription>
                            {saveIssueModalContent?.message || 'Please review the issues below and try again.'}
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[55vh] pr-4">
                        <div className="space-y-4">
                            {saveIssueModalContent?.because?.length ? (
                                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                                    <div className="text-sm font-semibold mb-1">Because</div>
                                    <ul className="space-y-1 text-sm text-muted-foreground list-disc pl-5">
                                        {saveIssueModalContent.because.map((reason, index) => (
                                            <li key={`${reason}-${index}`}>{reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}
                            {(saveIssueModalContent?.sections || []).map((section) => (
                                <div key={section.title} className="rounded-lg border p-3">
                                    <div className="text-sm font-semibold mb-2">{section.title}</div>
                                    <ul className="space-y-1 text-sm text-muted-foreground list-disc pl-5">
                                        {section.items.map((item, index) => (
                                            <li key={`${section.title}-${index}`}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveIssueModalOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
